/**
 * Permission Dependency Map — RBAC V1
 *
 * Defines prerequisite relationships between permissions.
 * When enabling a permission, its prerequisites are auto-enabled.
 * When disabling a permission, its dependents are auto-disabled.
 */

import { P, PERMISSION_LABELS, type PermissionKey, type PermissionMap } from './permission-keys';

// ─── Each key lists its PREREQUISITES (must be ON for this to work) ───

export const PERMISSION_PREREQUISITES: Partial<Record<PermissionKey, PermissionKey[]>> = {
  // ── Projects ──────────────────────────────────────────────────────
  // View project requires view assigned
  [P.PROJECTS_VIEW_PROJECT]: [P.PROJECTS_VIEW_ASSIGNED],
  // View all requires view assigned
  [P.PROJECTS_VIEW_ALL]: [P.PROJECTS_VIEW_ASSIGNED],
  // CRUD requires view all
  [P.PROJECTS_CREATE]: [P.PROJECTS_VIEW_ALL],
  [P.PROJECTS_EDIT]: [P.PROJECTS_VIEW_ALL],
  [P.PROJECTS_ARCHIVE]: [P.PROJECTS_VIEW_ALL],
  [P.PROJECTS_DELETE]: [P.PROJECTS_VIEW_ALL],
  [P.PROJECTS_VIEW_FINANCIALS]: [P.PROJECTS_VIEW_ASSIGNED],
  // Overview requires view project
  [P.PROJECTS_VIEW_OVERVIEW]: [P.PROJECTS_VIEW_PROJECT],
  // Team management requires view team + view project
  [P.PROJECTS_VIEW_TEAM]: [P.PROJECTS_VIEW_PROJECT],
  [P.PROJECTS_TEAM_ADD]: [P.PROJECTS_VIEW_TEAM, P.PROJECTS_VIEW_PROJECT],
  [P.PROJECTS_TEAM_EDIT]: [P.PROJECTS_VIEW_TEAM, P.PROJECTS_VIEW_PROJECT],
  [P.PROJECTS_TEAM_REMOVE]: [P.PROJECTS_VIEW_TEAM, P.PROJECTS_VIEW_PROJECT],
  [P.PROJECTS_VIEW_APPLY_ALL]: [P.PROJECTS_VIEW_TEAM, P.PROJECTS_VIEW_PROJECT],

  // ── Modules — CRUD requires VIEW ─────────────────────────────────
  [P.HIERARCHY_MODULE_VIEW]: [P.PROJECTS_VIEW_PROJECT],
  [P.HIERARCHY_MODULE_CREATE]: [P.HIERARCHY_MODULE_VIEW],
  [P.HIERARCHY_MODULE_EDIT]: [P.HIERARCHY_MODULE_VIEW],
  [P.HIERARCHY_MODULE_DELETE]: [P.HIERARCHY_MODULE_VIEW],

  // ── Features — CRUD requires VIEW ────────────────────────────────
  [P.HIERARCHY_FEATURE_VIEW]: [P.PROJECTS_VIEW_PROJECT],
  [P.HIERARCHY_FEATURE_CREATE]: [P.HIERARCHY_FEATURE_VIEW],
  [P.HIERARCHY_FEATURE_EDIT]: [P.HIERARCHY_FEATURE_VIEW],
  [P.HIERARCHY_FEATURE_DELETE]: [P.HIERARCHY_FEATURE_VIEW],

  // ── Tasks ────────────────────────────────────────────────────────
  [P.HIERARCHY_TASK_STATUS]: [P.PROJECTS_VIEW_PROJECT],
  [P.HIERARCHY_TASK_DELETE_OWN]: [P.PROJECTS_VIEW_PROJECT],
  [P.HIERARCHY_TASK_CREATE]: [P.HIERARCHY_TASK_STATUS],
  [P.HIERARCHY_TASK_ASSIGN]: [P.HIERARCHY_TASK_STATUS],
  [P.HIERARCHY_TASK_DELETE]: [P.HIERARCHY_TASK_STATUS],

  // ── Meetings — CRUD requires VIEW ────────────────────────────────
  [P.MEETINGS_VIEW]: [P.PROJECTS_VIEW_PROJECT],
  [P.MEETINGS_VIEW_ALL]: [P.MEETINGS_VIEW],
  [P.MEETINGS_CREATE]: [P.MEETINGS_VIEW],
  [P.MEETINGS_EDIT]: [P.MEETINGS_VIEW],
  [P.MEETINGS_DELETE]: [P.MEETINGS_VIEW],
  [P.MEETINGS_CANCEL]: [P.MEETINGS_VIEW],
  [P.MEETINGS_CLARA_ENABLE]: [P.MEETINGS_VIEW],
  [P.MEETINGS_CLARA_INSIGHTS]: [P.MEETINGS_VIEW],
  [P.MEETINGS_VIEW_MOM]: [P.MEETINGS_VIEW],

  // ── Settings — manage requires view ──────────────────────────────
  [P.SETTINGS_PROJECT_VIEW]: [P.PROJECTS_VIEW_PROJECT],
  [P.SETTINGS_PROJECT_CONFIG]: [P.SETTINGS_PROJECT_VIEW],
  [P.SETTINGS_MANAGE_GENERAL]: [P.SETTINGS_VIEW],
  [P.SETTINGS_MANAGE_MASTERS]: [P.SETTINGS_VIEW],
  [P.SETTINGS_MANAGE_CONFIG]: [P.SETTINGS_VIEW],
  // Managing keys you cannot see is not a role anyone means to build.
  //
  // Deliberately NOT `[P.SETTINGS_VIEW]`: that key is the settings UMBRELLA —
  // every tab's row is visible to whoever holds it — so requiring it here would
  // turn "give this member the API access tab" into "give them Roles &
  // Permissions and every master list too". Reaching the tab is the rail's job
  // (SETTINGS_RAIL_PERMISSIONS in the shell), which opens for this key alone.
  [P.SETTINGS_API_KEYS_MANAGE]: [P.SETTINGS_API_KEYS_VIEW],
  [P.PROJECT_TEMPLATES_MANAGE]: [P.PROJECT_TEMPLATES_VIEW],

  // ── Spark — create & vote requires feed access ─────────────────────

  // ── Time Tracking ────────────────────────────────────────────────
  [P.TIME_TRACK_MANUAL]: [P.TIME_TRACK_OWN],
  [P.TIME_VIEW_ALL]: [P.TIME_VIEW_TEAM],
  [P.TIME_EDIT_TEAM]: [P.TIME_VIEW_TEAM],
  [P.TIME_VIEW_RATES]: [P.TIME_VIEW_TEAM],

  // ── Timesheets ───────────────────────────────────────────────────
  [P.TIMESHEET_SUBMIT_OWN]: [P.TIMESHEET_VIEW_OWN, P.TIME_TRACK_OWN],
  // [P.TIMESHEET_REVIEW_TEAM]: [P.TIMESHEET_VIEW_OWN], // temporarily disabled — no team assignment per project yet
  [P.TIMESHEET_REVIEW_PROJECT]: [P.TIMESHEET_VIEW_OWN],
  // TIMESHEET_REVIEW_TEAM temporarily disabled — no team assignment per project yet
  [P.TIMESHEET_REVIEW_ALL]: [P.TIMESHEET_REVIEW_PROJECT],
  [P.TIMESHEET_APPROVE_REJECT]: [P.TIMESHEET_REVIEW_PROJECT],

  // ── Users ────────────────────────────────────────────────────────
  [P.USERS_VIEW_PROFILE]: [P.USERS_VIEW_DIRECTORY],
  [P.USERS_INVITE]: [P.USERS_VIEW_DIRECTORY],
  [P.USERS_EDIT]: [P.USERS_VIEW_DIRECTORY, P.USERS_VIEW_PROFILE],
  [P.USERS_DEACTIVATE]: [P.USERS_VIEW_DIRECTORY, P.USERS_VIEW_PROFILE],
  [P.USERS_REMOVE]: [P.USERS_VIEW_DIRECTORY, P.USERS_VIEW_PROFILE],
  [P.USERS_MANAGE_ROLES]: [P.USERS_VIEW_DIRECTORY, P.USERS_VIEW_PROFILE],

  // ── Reports ──────────────────────────────────────────────────────
  // Downloading a report you cannot open is not a role anyone means to build.
  [P.MY_TEAM_REPORT_EXPORT]: [P.MY_TEAM_REPORT_VIEW],

  // ── Clara AI ─────────────────────────────────────────────────────
  [P.CLARA_BRD_ANALYZER]: [P.PROJECTS_VIEW_PROJECT],
};

/**
 * Get all transitive prerequisites for a permission (recursive).
 * E.g. TIMESHEET_REVIEW_ALL → [REVIEW_TEAM, REVIEW_PROJECT, VIEW_OWN]
 */
export function getAllPrerequisites(key: PermissionKey): PermissionKey[] {
  const result = new Set<PermissionKey>();
  const stack = [...(PERMISSION_PREREQUISITES[key] ?? [])];

  while (stack.length > 0) {
    const current = stack.pop()!;
    if (!result.has(current)) {
      result.add(current);
      const parents = PERMISSION_PREREQUISITES[current];
      if (parents) {
        for (const p of parents) {
          if (!result.has(p)) stack.push(p);
        }
      }
    }
  }

  return Array.from(result);
}

/**
 * Get all permissions that directly or transitively depend on this key.
 * E.g. PROJECTS_VIEW_ALL → [CREATE, EDIT, DELETE, MANAGE_TEAM]
 */
export function getAllDependents(key: PermissionKey): PermissionKey[] {
  const result = new Set<PermissionKey>();
  const stack = [key];

  while (stack.length > 0) {
    const current = stack.pop()!;
    for (const [perm, prereqs] of Object.entries(PERMISSION_PREREQUISITES)) {
      if (prereqs?.includes(current) && !result.has(perm as PermissionKey)) {
        result.add(perm as PermissionKey);
        stack.push(perm as PermissionKey);
      }
    }
  }

  return Array.from(result);
}

/** True if all transitive prerequisites for `key` are currently enabled */
export function canEnablePermissionKey(key: PermissionKey, permissions: PermissionMap): boolean {
  const prereqs = getAllPrerequisites(key);
  return prereqs.every((p) => permissions[p] === true);
}

/**
 * Resolve enabling a permission: returns updated map with all prerequisites enabled.
 */
export function resolveEnablePermission(
  key: PermissionKey,
  currentMap: PermissionMap,
): PermissionMap {
  const next = { ...currentMap, [key]: true };
  const prereqs = getAllPrerequisites(key);
  for (const p of prereqs) {
    next[p] = true;
  }
  return next;
}

/**
 * Resolve disabling a permission: returns updated map with all dependents disabled.
 * Also returns the list of dependents that were auto-disabled.
 */
export function resolveDisablePermission(
  key: PermissionKey,
  currentMap: PermissionMap,
): { map: PermissionMap; disabledDependents: PermissionKey[] } {
  const next = { ...currentMap, [key]: false };
  const dependents = getAllDependents(key);
  const disabledDependents: PermissionKey[] = [];

  for (const d of dependents) {
    if (next[d]) {
      next[d] = false;
      disabledDependents.push(d);
    }
  }

  return { map: next, disabledDependents };
}

/**
 * Resolve enabling all keys in a module: auto-enables prerequisites across modules.
 */
export function resolveEnableModule(
  keys: readonly PermissionKey[],
  currentMap: PermissionMap,
): PermissionMap {
  const next = { ...currentMap };
  for (const k of keys) {
    next[k] = true;
    const prereqs = getAllPrerequisites(k);
    for (const p of prereqs) {
      next[p] = true;
    }
  }
  return next;
}

/**
 * Resolve disabling all keys in a module: auto-disables dependents.
 */
export function resolveDisableModule(
  keys: readonly PermissionKey[],
  currentMap: PermissionMap,
): { map: PermissionMap; disabledDependents: PermissionKey[] } {
  const next = { ...currentMap };
  const allDisabled = new Set<PermissionKey>();

  for (const k of keys) {
    next[k] = false;
    allDisabled.add(k);
    const dependents = getAllDependents(k);
    for (const d of dependents) {
      if (next[d]) {
        next[d] = false;
        allDisabled.add(d);
      }
    }
  }

  // Only report dependents that were NOT in the original module keys
  const keySet = new Set<PermissionKey>(keys as unknown as PermissionKey[]);
  const disabledDependents = Array.from(allDisabled).filter((k) => !keySet.has(k));

  return { map: next, disabledDependents };
}

/**
 * Get labels of active permissions that require this key as a prerequisite.
 * Used for the "Required by: X, Y" tooltip.
 */
export function getRequiredByLabels(key: PermissionKey, currentMap: PermissionMap): string[] {
  const dependents = getAllDependents(key);
  return dependents.filter((d) => currentMap[d]).map((d) => PERMISSION_LABELS[d]);
}
